import React, { useMemo } from "react";
import { Box } from "@mui/material";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { animationPreviewUrlBySlug } from "config/vetstoreAssets";
import { highQualityLottieProps } from "config/lottieQuality";
import { StoreItem } from "types/vetcoin";

const DotLottieReactComponent =
  DotLottieReact as unknown as React.ComponentType<any>;

export type AnimationPresentationType = "pop-up" | "solo" | "inline";
export type AnimationPresentationSize =
  | "small"
  | "medium"
  | "large"
  | "fullscreen"
  | "multiple";
export type AnimationPresentationTransition =
  | "still"
  | "growing"
  | "diagonal"
  | "up"
  | "down";

export type AnimationPresentationConfig = {
  type: AnimationPresentationType;
  size: AnimationPresentationSize;
  transition: AnimationPresentationTransition;
};

const DEFAULT_ANIMATION_PRESENTATION: AnimationPresentationConfig = {
  type: "pop-up",
  size: "small",
  transition: "still",
};

const normalizePresentationType = (value: string) => {
  const normalized = value.trim().toLowerCase().replace(/\s+/g, "-");
  if (normalized === "popup") return "pop-up";
  if (normalized === "pop-up" || normalized === "solo" || normalized === "inline") {
    return normalized as AnimationPresentationType;
  }
  return null;
};

const normalizePresentationSize = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (
    normalized === "multiple" ||
    normalized === "milti" ||
    normalized === "miltiple"
  ) {
    return "multiple" as AnimationPresentationSize;
  }
  if (
    normalized === "small" ||
    normalized === "medium" ||
    normalized === "large" ||
    normalized === "fullscreen"
  ) {
    return normalized as AnimationPresentationSize;
  }
  return null;
};

const normalizePresentationTransition = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (
    normalized === "still" ||
    normalized === "growing" ||
    normalized === "diagonal" ||
    normalized === "up" ||
    normalized === "down"
  ) {
    return normalized as AnimationPresentationTransition;
  }
  return null;
};

export const parseAnimationPresentation = (
  rawValue?: string | null,
): AnimationPresentationConfig => {
  if (!rawValue) return DEFAULT_ANIMATION_PRESENTATION;
  const [rawType, rawSize, rawTransition] = rawValue.split(",");
  return {
    type: normalizePresentationType(rawType || "") || DEFAULT_ANIMATION_PRESENTATION.type,
    size: normalizePresentationSize(rawSize || "") || DEFAULT_ANIMATION_PRESENTATION.size,
    transition:
      normalizePresentationTransition(rawTransition || "") ||
      DEFAULT_ANIMATION_PRESENTATION.transition,
  };
};

const baseSizeByVariant: Record<AnimationPresentationSize, string> = {
  small: "88px",
  medium: "140px",
  large: "220px",
  fullscreen: "min(72vw, 72vh)",
  multiple: "120px",
};

const popUpCopies = [
  { top: "10%", left: "12%", scale: 0.85, delay: "0ms" },
  { top: "20%", left: "78%", scale: 1.1, delay: "200ms" },
  { top: "34%", left: "32%", scale: 0.7, delay: "350ms" },
  { top: "52%", left: "70%", scale: 1.3, delay: "120ms" },
  { top: "66%", left: "18%", scale: 1.15, delay: "420ms" },
  { top: "76%", left: "58%", scale: 0.75, delay: "260ms" },
];

const getTransitionAnimation = (transition: AnimationPresentationTransition) => {
  if (transition === "growing") return "vetAnimGrow 1200ms ease-in-out infinite";
  if (transition === "diagonal")
    return "vetAnimDiagonal 1400ms linear infinite alternate";
  if (transition === "up") return "vetAnimUp 1200ms ease-in-out infinite";
  if (transition === "down") return "vetAnimDown 1200ms ease-in-out infinite";
  return "none";
};

type EquippedAnimationPresentationProps = {
  item?: StoreItem | null;
};

export const EquippedAnimationPresentation: React.FC<
  EquippedAnimationPresentationProps
> = ({ item }) => {
  const animationSrc = useMemo(() => {
    if (!item?.slug) return null;
    return animationPreviewUrlBySlug[item.slug] || null;
  }, [item?.slug]);
  const presentation = useMemo(
    () => parseAnimationPresentation(item?.presentation),
    [item?.presentation],
  );

  if (!animationSrc) return null;

  const baseSize = baseSizeByVariant[presentation.size];
  const animationName = getTransitionAnimation(presentation.transition);
  const popUpScaleBySize: number[] =
    presentation.size === "multiple"
      ? [0.55, 0.78, 1, 1.25, 1.45, 0.9]
      : [1, 1, 1, 1, 1, 1];

  if (presentation.type === "solo") {
    return (
      <Box
        sx={{
          pointerEvents: "none",
          position: "fixed",
          inset: 0,
          zIndex: 1200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: baseSize,
            height: baseSize,
            animation: animationName,
            transformOrigin: "center",
            opacity: 0.95,
          }}
        >
          <DotLottieReactComponent src={animationSrc} {...highQualityLottieProps} />
        </Box>
        <Box
          sx={{
            "@keyframes vetAnimGrow": {
              "0%": { transform: "scale(0.7)", opacity: 0.4 },
              "50%": { transform: "scale(1.12)", opacity: 1 },
              "100%": { transform: "scale(0.7)", opacity: 0.4 },
            },
            "@keyframes vetAnimDiagonal": {
              "0%": { transform: "translate(18vw, -18vh) scale(0.9)", opacity: 0.5 },
              "100%": {
                transform: "translate(-18vw, 18vh) scale(1.05)",
                opacity: 1,
              },
            },
          }}
        />
      </Box>
    );
  }

  if (presentation.type === "inline") {
    return (
      <Box
        sx={{
          pointerEvents: "none",
          position: "fixed",
          right: 20,
          bottom: 20,
          width: baseSize,
          height: baseSize,
          zIndex: 1200,
          animation: animationName,
          opacity: 0.95,
        }}
      >
        <DotLottieReactComponent src={animationSrc} {...highQualityLottieProps} />
        <Box
          sx={{
            "@keyframes vetAnimGrow": {
              "0%": { transform: "scale(0.7)", opacity: 0.4 },
              "50%": { transform: "scale(1.12)", opacity: 1 },
              "100%": { transform: "scale(0.7)", opacity: 0.4 },
            },
            "@keyframes vetAnimDiagonal": {
              "0%": { transform: "translate(12vw, -12vh) scale(0.9)", opacity: 0.45 },
              "100%": {
                transform: "translate(-12vw, 12vh) scale(1.05)",
                opacity: 1,
              },
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        pointerEvents: "none",
        position: "fixed",
        inset: 0,
        zIndex: 1200,
      }}
    >
      {popUpCopies.map((copy, index) => (
        <Box
          key={`${copy.top}-${copy.left}`}
          sx={{
            position: "absolute",
            top: copy.top,
            left: copy.left,
            width: `calc(${baseSize} * ${copy.scale * popUpScaleBySize[index]})`,
            height: `calc(${baseSize} * ${copy.scale * popUpScaleBySize[index]})`,
            animation: animationName,
            animationDelay: copy.delay,
            transformOrigin: "center",
            opacity: 0.95,
          }}
        >
          <DotLottieReactComponent
            src={animationSrc}
            key={`${animationSrc}-${index}`}
            {...highQualityLottieProps}
          />
        </Box>
      ))}
      <Box
        sx={{
          "@keyframes vetAnimGrow": {
            "0%": { transform: "scale(0.72)", opacity: 0.45 },
            "50%": { transform: "scale(1.2)", opacity: 1 },
            "100%": { transform: "scale(0.72)", opacity: 0.45 },
          },
          "@keyframes vetAnimDiagonal": {
            "0%": { transform: "translate(22vw, -22vh) scale(0.8)", opacity: 0.4 },
            "100%": { transform: "translate(-22vw, 22vh) scale(1.15)", opacity: 1 },
          },
          "@keyframes vetAnimUp": {
            "0%": { transform: "translateY(10vh) scale(0.88)", opacity: 0.5 },
            "100%": { transform: "translateY(-10vh) scale(1.1)", opacity: 1 },
          },
          "@keyframes vetAnimDown": {
            "0%": { transform: "translateY(-10vh) scale(0.88)", opacity: 0.5 },
            "100%": { transform: "translateY(10vh) scale(1.1)", opacity: 1 },
          },
        }}
      />
    </Box>
  );
};
